import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  ColDef,
  GetQuickFilterTextParams,
  ICellRendererParams,
  ISetFilterParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import _ from 'lodash'

import { Artifact, DocumentSource } from '@/gen/artifacts/v1/model_pb'

import { toDocumentStatus } from '@/const/artifact-status.types'
import { documentSourceLabel, documentStatusLabel } from '@/const/label'
import { documentStatusPriority } from '@/const/priority'

import { formatTimestamp } from '@/lib/date/date'
import { MAX_STRING } from '@/lib/string/string-utils'

import { ArtifactActionCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-action-cell'
import { ArtifactAnalysisStatusCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-analysis-status-cell'
import { ArtifactCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-cell'
import { ArtifactDateAddedCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-date-added-cell'
import { ArtifactExpiryDateCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-expiry-date-cell'
import { ArtifactLinkCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-link-cell'
import { ArtifactSourceCell } from '@/pages/company-drawer/artifact/ag-grid-artifact/components/cells/artifact-source-cell'
import type { ArtifactRow } from '@/pages/company-drawer/artifact/ag-grid-artifact/types'

// Column widths
const WIDTH_ARTIFACT = 230
const WIDTH_ARTIFACT_EXPORT = 130
const WIDTH_SOURCE = 177
const WIDTH_LINK = 260
const WIDTH_LINK_EXPORT = 160
const WIDTH_ANALYSIS_STATUS = 220
const WIDTH_DATE_ADDED = 124
const WIDTH_DATE_ADDED_EXPORT = 110
const WIDTH_EXPIRY_DATE = 150
const WIDTH_ACTION = 50

export const getArtifactColumnDefs = (
  artifacts: Artifact[] = [],
  artifactSources: DocumentSource[] = [],
  readOnly = false,
  isExport = false,
): ColDef<ArtifactRow>[] => {
  const cols: ColDef<ArtifactRow>[] = [
    {
      colId: 'artifactSubcategory',
      field: 'artifactSubcategory',
      headerName: 'Artifact',
      // Use fixed width for export to prevent expansion, flex for normal view
      ...(isExport ? { width: WIDTH_ARTIFACT_EXPORT } : { minWidth: WIDTH_ARTIFACT, flex: 1 }),
      cellRenderer: (params: ICellRendererParams<ArtifactRow>) => (
        <ArtifactCell {...params} readOnly={readOnly} />
      ),
      filter: 'agSetColumnFilter',
      filterParams: {
        values: _.uniq(artifacts.map((artifact) => artifact.artifactSubcategory)),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ArtifactRow>) =>
        params.data?.artifactSubcategory || '',
      sortable: true,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const subcategoryA = nodeA?.data?.artifactSubcategory || ''
        const subcategoryB = nodeB?.data?.artifactSubcategory || ''
        return subcategoryA.localeCompare(subcategoryB)
      },
      cellDataType: false,
    },
    {
      colId: 'source',
      field: 'source',
      headerName: 'Source',
      width: WIDTH_SOURCE,
      cellRenderer: ArtifactSourceCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: Object.values(DocumentSource)
          .filter((v) => typeof v === 'number')
          .filter((src) => artifactSources.includes(src))
          .map((v) => documentSourceLabel[v as DocumentSource]),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ArtifactRow>) =>
        params.data?.source ? documentSourceLabel[params.data.source] || '' : '',
      sortable: true,
      cellDataType: false,
    },
    {
      colId: 'link',
      headerName: 'Link',
      // Use fixed width for export to prevent expansion, flex for normal view
      ...(isExport ? { width: WIDTH_LINK_EXPORT } : { minWidth: WIDTH_LINK, flex: 2 }),
      cellRenderer: (params: ICellRendererParams<ArtifactRow>) => (
        <ArtifactLinkCell {...params} isExport={isExport} />
      ),
      getQuickFilterText: (params: GetQuickFilterTextParams<ArtifactRow>) => {
        const { name, artifactSubcategory } = params.data || {}
        return `${name || ''} ${artifactSubcategory || ''}`
      },
      valueGetter: (params: ValueGetterParams<ArtifactRow>) => {
        const name = params.data?.name
        return name === 'Unknown' ? MAX_STRING : name || ''
      },
      sortable: true,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const nameA = nodeA?.data?.name ?? ''
        const nameB = nodeB?.data?.name ?? ''
        const sortNameA = nameA === 'Unknown' ? MAX_STRING : nameA
        const sortNameB = nameB === 'Unknown' ? MAX_STRING : nameB
        return sortNameA.localeCompare(sortNameB)
      },
      cellDataType: false,
    },
    {
      colId: 'analysisStatus',
      headerName: 'Analysis status',
      width: WIDTH_ANALYSIS_STATUS,
      minWidth: WIDTH_ANALYSIS_STATUS,
      hide: readOnly,
      cellRenderer: ArtifactAnalysisStatusCell,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: _.uniq(
          artifacts.map((artifact) =>
            artifact.error ? 'Failed' : documentStatusLabel[toDocumentStatus(artifact.status)],
          ),
        ),
        suppressMiniFilter: true,
        selectAllOnMiniFilter: true,
        suppressSyncValuesAfterDataChange: true,
      } as ISetFilterParams,
      valueGetter: (params: ValueGetterParams<ArtifactRow>) => {
        const { status, error } = params.data ?? {}
        return error ? 'Failed' : documentStatusLabel[toDocumentStatus(status)]
      },
      sortable: true,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const maxStatus = _.max(Object.values(documentStatusPriority)) ?? 0
        const errorA = nodeA?.data?.error
        const errorB = nodeB?.data?.error
        const statusA = toDocumentStatus(nodeA?.data?.status)
        const statusB = toDocumentStatus(nodeB?.data?.status)

        const priorityA = errorA ? maxStatus + 1 : documentStatusPriority[statusA]
        const priorityB = errorB ? maxStatus + 1 : documentStatusPriority[statusB]

        return priorityA - priorityB
      },
      cellDataType: false,
    },
    {
      colId: 'dateAdded',
      field: 'dateAdded',
      headerName: 'Date added',
      width: isExport ? WIDTH_DATE_ADDED_EXPORT : WIDTH_DATE_ADDED,
      cellRenderer: ArtifactDateAddedCell,
      valueGetter: (params: ValueGetterParams<ArtifactRow>) => {
        const { dateAdded, dateUpdated } = params.data ?? {}
        return dateAdded ?? dateUpdated
      },
      valueFormatter: (params: ValueFormatterParams<ArtifactRow>) => {
        if (!params.value) return ''
        return formatTimestamp(params.value) ?? ''
      },
      sortable: true,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const dateA = nodeA?.data?.dateAdded ?? nodeA?.data?.dateUpdated
        const dateB = nodeB?.data?.dateAdded ?? nodeB?.data?.dateUpdated

        if (!dateA && !dateB) return 0
        if (!dateA) return 1
        if (!dateB) return -1

        const timeA = timestampDate(dateA).getTime()
        const timeB = timestampDate(dateB).getTime()
        return timeB - timeA // Most recent first
      },
      filter: 'agDateColumnFilter',
      cellDataType: false,
    },
    {
      colId: 'expiryDate',
      field: 'expirationDate',
      headerName: 'Expiry date',
      width: WIDTH_EXPIRY_DATE,
      hide: !readOnly,
      cellRenderer: ArtifactExpiryDateCell,
      valueGetter: (params: ValueGetterParams<ArtifactRow>) => params.data?.expirationDate,
      valueFormatter: (params: ValueFormatterParams<ArtifactRow>) => {
        if (!params.value) return '–'
        return formatTimestamp(params.value) ?? '–'
      },
      sortable: true,
      comparator: (_valueA, _valueB, nodeA, nodeB) => {
        const dateA = nodeA?.data?.expirationDate
        const dateB = nodeB?.data?.expirationDate

        if (!dateA && !dateB) return 0
        if (!dateA) return 1
        if (!dateB) return -1

        const timeA = timestampDate(dateA).getTime()
        const timeB = timestampDate(dateB).getTime()
        return timeA - timeB
      },
      filter: 'agDateColumnFilter',
      cellDataType: false,
    },
    {
      colId: 'action',
      headerName: '',
      width: isExport ? 0 : WIDTH_ACTION,
      hide: readOnly,
      cellRenderer: ArtifactActionCell,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: 'right',
      cellDataType: false,
    },
  ]

  return cols
}
